/**
 * Patient Access (PAC) intro-call script — the four sections a rep works while
 * an inbound caller is on the line (BAR-2612).
 *
 * Replaces the nine-section inbound script that shared a tail with the scheduled
 * intro call. That tail walked a caller through the whole pitch — procedure,
 * explanation, location, price — before it offered anything. An inbound caller
 * has not asked for a pitch: they rang with a question. So the card is the job
 * this call actually does. **Answer the question well enough to be trusted, then
 * get them onto a consultation.** Everything the old tail explained now lives in
 * the FAQ panels, read only when asked for.
 *
 * ENG-OWNED, like `patient-access-script.ts`: a typed definition in the repo
 * rather than a FHIR Questionnaire, so wording changes go through a PR.
 *
 * SCRIPT LINES ARE STATIC. `[rep]`, `[name]`, `[city]`, `[facility]` and
 * `[provider name]` render literally, so the team reads the same sentence on
 * every call. Prices are the exception and are real numbers, because the FAQ
 * canon quotes them out loud — they live in `PAC_PROCEDURE_PRICES`.
 *
 * SCRIPT ACTIONS CARRY NO CONTROL, as on the PN consultation: the rep reads
 * them, and only what is worth charting gets an input. The one typed field is
 * the fun-facts note. There are no checkboxes on this card and no section ticks.
 */

import { PacBeat, PacWidget } from './patient-access-script';
import { PN_BARRIER_OPTIONS, PN_MOTIVATION_OPTIONS } from './pn-consultation-script';

/* -------------------------------------------------------------------------
   Option vocabularies
   ------------------------------------------------------------------------- */

/**
 * The rep's read of how the call went.
 *
 * Deliberately not the PN consultation's ladder. That call ends in a payment
 * decision, so its outcomes measure closeness to paying; this one ends in a
 * booking, so its outcomes measure how much of a conversation there was to build
 * on. `Booked` and `To send a booking link` — the old vocabulary — are gone;
 * notes already carrying them still display, because the select keeps an entry
 * for a stored value the list no longer offers.
 */
export const PAC_INTRO_CALL_OUTCOME_OPTIONS = [
  'Consultation booked',
  'Meaningful conversation, to follow up',
  'Not ready for a consult yet but could be in the future',
  'Short conversation, unclear their interest level',
  'Not interested',
] as const;
export type PacIntroCallOutcome = (typeof PAC_INTRO_CALL_OUTCOME_OPTIONS)[number];

/** The one outcome that reads as a positive close in the collapsed preview. */
export const PAC_INTRO_CALL_BOOKED_OUTCOME: PacIntroCallOutcome = 'Consultation booked';

/* -------------------------------------------------------------------------
   Long-form script copy. Patient-facing register.
   ------------------------------------------------------------------------- */

const CONSULT =
  'The best next step is a free consultation with one of our patient navigators. They’ll go through your history properly, answer the medical questions I can’t, and tell you whether this is likely to work for you. There’s no pressure and nothing to pay. I have times as soon as today — would you like me to get you in?';

const WHERE =
  'Before I look at times — whereabouts are you? I want to make sure I’m giving you these in your own time zone.';

const EXPECT =
  'It’s about 25 minutes over Zoom. We’ll email you the link straight after this call, along with a reminder. If something comes up, the confirmation has a link to move it.';

const BRIDGE =
  'That’s the short version — the honest answer is that the rest depends on your history, and that’s exactly what the consultation is for.';

/* -------------------------------------------------------------------------
   FAQs — the Patient Access FAQ, transcribed
   ------------------------------------------------------------------------- */

/**
 * List price per procedure, all-inclusive. One constant, because it is quoted in
 * two FAQs and a price change must not be a search-and-replace.
 *
 * Patient-facing names, not the internal short forms: this is copy the rep says
 * out loud.
 */
export const PAC_PROCEDURE_PRICES: readonly { procedure: string; price: string }[] = [
  { procedure: 'ESG', price: '$12,995' },
  { procedure: 'ESG Max', price: '$15,995' },
  { procedure: 'Gastric balloon', price: '$7,495' },
  { procedure: 'Revision of a prior sleeve or bypass', price: '$11,995' },
];

/**
 * The twelve procedure centres, as a rep needs them on a call: metro, where it
 * actually is, and who operates there.
 *
 * Hard-coded rather than read from Medplum `Location`s on purpose. This is the
 * *marketing* answer to "where are you?" — the facility name and the published
 * physician — which is a different fact from the scheduling location the
 * consultation is booked against, and it must not change under a rep mid-call
 * because a Location was edited.
 */
export const PAC_CENTERS: readonly { metro: string; facility: string; town: string; physicians: string }[] = [
  {
    metro: 'Boston, MA',
    facility: 'Emerson Hospital',
    town: 'Concord, MA',
    physicians: 'Dr. Christopher Thompson, Dr. Pichamol Jirapinyo',
  },
  {
    metro: 'Charleston, SC',
    facility: 'Charleston Surgery Center',
    town: 'Summerville, SC',
    physicians: 'Dr. Daniel Szvarca, Dr. Pichamol Jirapinyo',
  },
  {
    metro: 'Charlotte, NC',
    facility: 'Iredell Surgery at Mooresville',
    town: 'Mooresville, NC',
    physicians: 'Dr. Christopher McGowan',
  },
  {
    metro: 'Dallas, TX',
    facility: 'Coppell Surgery Center',
    town: 'Coppell, TX',
    physicians: 'Dr. Shehriyar Mehershahi, Dr. Christopher McGowan',
  },
  {
    metro: 'Detroit, MI',
    facility: 'Surgeons Choice Medical Center',
    town: 'Southfield, MI',
    physicians: 'Dr. Mustafa Huseini',
  },
  {
    metro: 'Houston, TX',
    facility: 'Texas International Endoscopy Center',
    town: 'Houston, TX',
    physicians: 'Dr. Julien Fahed',
  },
  {
    metro: 'Jacksonville, FL',
    facility: 'Ascension St. Vincent’s St. Johns County',
    town: 'St. Johns, FL',
    physicians: 'Dr. Lindsey Merritt',
  },
  { metro: 'Miami, FL', facility: 'Surgicare of Miramar', town: 'Miramar, FL', physicians: 'Dr. Zaid Rana' },
  {
    metro: 'Nashville, TN',
    facility: 'Tennessee Surgery Center',
    town: 'Nashville, TN',
    physicians: 'Dr. Zachary Callahan',
  },
  {
    metro: 'Tampa, FL',
    facility: 'BayCare Hospital Wesley Chapel',
    town: 'Wesley Chapel, FL',
    physicians: 'Dr. Ali Abbas',
  },
  {
    metro: 'Temecula Valley, CA',
    facility: 'United Surgery Center Temecula',
    town: 'Temecula, CA',
    physicians: 'Dr. Brian Lim',
  },
  {
    metro: 'Washington, DC',
    facility: 'Gut Theory Total Digestive Care',
    town: 'Washington, DC',
    physicians: 'Dr. Janese Laster-Butler',
  },
];

/** One line per centre, for the FAQ that has to read the list out. */
const CENTER_LINES = PAC_CENTERS.map(
  (center) => `**${center.metro}** — ${center.facility}, ${center.town} · ${center.physicians}`
);

/**
 * What a rep can say about the physician at a caller's centre. Credentials, in
 * the register they are said in — not bios.
 */
const DOCTOR_LINES = [
  '**Dr. Rana — Miami:** already one of the top 10 ESG doctors in the country by volume despite being one of our younger physicians. Energetic, speaks Spanish, wonderful with patients. Now travels the country training other doctors in ESG.',
  '**Dr. Abbas — Tampa:** two years of full-time advanced fellowship at Harvard Medical School and Brigham and Women’s. Fulbright Scholar, multiple U.S. News Top Doctor recognitions in Tampa. Technically one of the strongest ESG doctors out there.',
  '**Dr. Merritt — Jacksonville:** one of the top three ESG doctors in the country by volume. Built her own practice around the procedure before joining Everself.',
  '**Dr. Huseini — Detroit:** two years of full-time advanced fellowship at Harvard Medical School, trained directly with Dr. Thompson, who invented ESG. One of relatively few doctors with that level of formal hands-on ESG training.',
  '**Dr. Laster — Washington, DC:** quadruple-board-certified — internal medicine, gastroenterology, obesity medicine and nutrition. Founded her own practice, Gut Theory, before joining Everself.',
  '**Dr. McGowan — Charlotte:** one of the top three ESG doctors in the world, 5,000+ procedures, Good Morning America and NBC. Helped invent ESG Max; patients travel from around the world to see him.',
  '**Dr. Jirapinyo — Boston:** assistant professor at Harvard Medical School and one of the top ESG and advanced-endoscopy physicians in the world. Chairs a major committee setting clinical guidelines for the field. With Dr. Thompson since ESG began in 2012. Very limited availability.',
  '**Dr. Thompson — Boston:** invented ESG and performed the first one in 2012. Full-time professor at Harvard Medical School, and the inventor of ESG, SIPS and TORe. Our co-founder.',
];

/**
 * The Patient Access FAQ, transcribed from the Top 10 doc.
 *
 * The order is the doc's, which is the order the questions arrive on a call —
 * cost first, because it is the most-asked question in the corpus and usually
 * the opening one.
 *
 * **Not paraphrased.** These are the answers the team is trained on, and a rep
 * reading a version this repo invented would be reading something no one signed
 * off. Where the doc gives a standard answer and branches, all of them ship: a
 * rep who cannot see which branch they are in reads the wrong one.
 *
 * Almost every response ends by offering the consultation. That is the doc's
 * design, not a gloss added here.
 */
export const PAC_INTRO_CALL_FAQS: readonly {
  question: string;
  asks?: readonly string[];
  responses: readonly { when?: string; say: string }[];
  facts?: readonly string[];
  never?: string;
}[] = [
  {
    question: 'How much does it cost?',
    asks: ['What’s the total cost?', 'How much is the procedure?', 'Is that everything, all in?', '¿Cuánto cuesta?'],
    responses: [
      {
        when: 'They haven’t named a procedure —',
        say: 'Our programs range from about $7,500 to $16,000 depending on the procedure, and that’s all-inclusive: the procedure, anesthesia, the facility, your blood work, a smart scale, and twelve months of care with a dietitian and a 24/7 clinical line. Financing starts as low as $167 a month with 0% interest options. Would you like to find a time for a free consult to discuss this with a patient navigator?',
      },
      {
        when: 'They name the procedure —',
        say: 'ESG is $12,995. That’s the full price, there’s nothing added on the day, and it includes twelve months of care afterward. Financing starts as low as $297 a month. Want me to walk you through how the payments work, or would it help to get you a consult time first?',
      },
      {
        when: '“Why is it so expensive?”, or comparing to a hospital quote —',
        say: 'It’s a fair question. The price covers everything: the physician, anesthesia, the surgery center, labs, and then a full year of follow-up care with your own dietitian and care team. A lot of what you’d pay for piece by piece elsewhere is already in it. And the only money due up front is your first monthly payment. Would it help to see what that monthly number looks like for you?',
      },
    ],
    facts: [
      'Exact prices: ' + PAC_PROCEDURE_PRICES.map((entry) => `**${entry.procedure}** ${entry.price}`).join(' · '),
      'One in nine cost questions is about the total — lead with all-inclusive.',
    ],
  },
  {
    question: 'Where are you located?',
    asks: ['Where are you guys located?', 'What’s the nearest place to me?', 'Do I have to travel?'],
    responses: [
      {
        when: 'Standard —',
        say: 'Happy to check the closest one to you. What city are you in? … That would be our [city] center at [facility], where Dr. [name] performs the procedure. And the good news is we only need you there for the day itself. Your consult and all twelve months of aftercare are virtual, from home.',
      },
      {
        when: 'They’re far from a center —',
        say: 'You’re about [X] hours from our [city] center, and that’s more common than you’d think. About one in four of our patients travel for their procedure, because the only in-person day is the procedure itself. Your consult is a video call, your blood work is done near you, and the whole year of follow-up is through the app and video visits. We also have partner hotels near every center with rates for our patients, so most people come in the day before, stay two nights, and head home the day after. Want me to set up the consult, and the navigator can walk you through the travel side?',
      },
      {
        when: '“Is it a hospital or an office?” —',
        say: 'It’s done at [facility], which is [an accredited outpatient surgery center / a hospital] in [town]. Free on-site parking, and you’re home the same day.',
      },
    ],
    facts: ['**Our 12 centers**', ...CENTER_LINES],
  },
  {
    question: 'Do you take insurance?',
    asks: ['Do you guys take insurance?', 'Can my insurance pay for this?', 'Is it covered by Medicare?'],
    responses: [
      {
        when: 'Standard —',
        say: 'We’re self-pay, so there’s no insurance approval process and no waiting. The price is flat and everything’s included. Most patients use financing or HSA/FSA funds, and a growing group of employers now cover it. Our patient navigator team can go over more details in a consult if you’d like.',
      },
      {
        when: 'Medicare or Medicaid —',
        say: 'Medicare and Medicaid don’t cover endoscopic weight loss procedures, so it would be self-pay, the same as for everyone. What most people in your position do is finance it. Payments start as low as $167 a month, and you can add a co-applicant, which often improves the options. Would it help to look at that together?',
      },
      {
        when: '“Can I use my HSA or FSA?” —',
        say: 'Yes. Eligible pre-tax HSA or FSA funds can go toward it, and you can combine that with financing for the rest. Your navigator will help you set that up at the consult.',
      },
      {
        when: 'Their insurance covers a different procedure —',
        say: 'Ours is a different path, no surgery and no waiting. Want to hear how it compares?',
      },
    ],
    never:
      'Never say “not yet” or “not covered yet” — it invites people to wait for coverage that isn’t coming. Never imply coverage before a benefit check, and never argue with someone whose insurance covers a different procedure.',
  },
  {
    question: 'Can I make payments?',
    asks: [
      'Do you have payment plans?',
      'Is there a down payment?',
      'How much would it be a month?',
      'Do you check credit?',
    ],
    responses: [
      {
        when: 'Standard —',
        say: 'Yes. We work with two financing partners, Cherry and CareCredit, and the payments start as low as $167 a month with 0% interest options. As little as about $300 locks in your date. Your navigator runs the options with you at the consult, so you’ll see real numbers before you decide anything.',
      },
      {
        when: 'Worried about credit —',
        say: 'Cherry starts with a soft check to show your options, so it doesn’t affect your credit score. And you can add a co-applicant, a spouse or family member, which often improves what you’re offered. A lot of people do that from the start. Want me to note that for your navigator?',
      },
      {
        when: 'Asking about 0% interest specifically —',
        say: 'Both partners have 0% interest options. CareCredit’s 0% is a 6-month term with us, and the longer plans carry interest, so the navigator will show you both so you can pick what fits. Cherry splits it into monthly payments as well. You’ll see the exact terms before you sign anything.',
      },
    ],
  },
  {
    question: 'Is it surgery? How does it work?',
    asks: ['Is this surgery?', 'Are you cutting me open?', 'Does it hurt?', 'How long until I’m back at work?'],
    responses: [
      {
        when: 'Standard —',
        say: 'It’s not surgery. There are no incisions and no scars. It’s done endoscopically, through the mouth, while you’re under anesthesia. The doctor places a series of sutures that fold your stomach in on itself, making it 60 to 75% smaller, so you feel full faster and stay full longer. The procedure takes about 60 minutes, you rest for an hour or two, and you go home the same day. After two or three days of rest you’re back to your regular schedule.',
      },
      {
        when: '“Does it hurt?” or “what’s the recovery like?” —',
        say: 'Most people describe the first couple of days as feeling like a stomach bug, some cramping and nausea, and the care team is checking on you through the first 72 hours. Most are back at work within three days. Your team gives you a plan for those days so you know exactly what to expect.',
      },
      {
        when: '“Is it safe?” —',
        say: 'Across more than 5,000 patients there have been zero deaths. Our physicians have performed more than 10,000 of these procedures. Whether it’s the right fit for you medically is something the clinical team confirms after your consult, and you’ll have a 24/7 nurse line the whole way through.',
      },
    ],
  },
  {
    question: 'What happens at the consult, and how soon can I get in?',
    asks: [
      'Is the consult virtual or in person?',
      'Do I have to pay for that?',
      'Am I talking to the doctor?',
      'When’s the next availability?',
    ],
    responses: [
      {
        when: 'Standard —',
        say: 'The consult is free, it’s about 25 minutes, and it’s a video call from home with one of our patient navigators. They know these procedures inside and out. They’ll go through your history and goals, get you the exact price and your financing options, and answer your questions. Nothing to decide on the call. We often have openings same-day. It looks like we have one today at [time], would that work?',
      },
      {
        when: '“Is it with the doctor?” —',
        say: 'The consult is with a patient navigator, who walks you through everything on the practical side: your options, timing, price, and payments. Anything that needs a medical answer goes to our clinical team, and you’ll meet your physician before your procedure day. Want me to grab you a time?',
      },
      {
        when: 'They want to come in person first —',
        say: 'The consult itself is virtual, that’s how everyone starts. If you’d like to see the center and meet the doctor before committing to anything, we can arrange that on a procedure day. Let’s get the consult booked first so you’ve got the full picture.',
      },
    ],
  },
  {
    question: 'What are the next steps? How soon could I have it done?',
    asks: [
      'What’s the next step?',
      'How soon could I do it?',
      'Do I need blood work first?',
      'Do I need a liquid diet before?',
    ],
    responses: [
      {
        when: 'Standard —',
        say: 'It’s four steps. First, the free consult, about 25 minutes by video. Second, our clinical team reviews your history and confirms you’re a candidate. Third, you pick your procedure date at your center and lock it in with your first payment. Fourth, before the day, you meet your care team and get a short prep plan. The navigator can see the calendar, so at the consult they’ll tell you exactly how soon you could go.',
      },
      {
        when: 'They want a real answer on timing —',
        say: 'Once you’re cleared, the timing is mostly down to which procedure day at your center you want. The navigator will show you the soonest ones on the consult. If you’ve got a date in mind, like before a trip or an event, tell them and they’ll work back from it.',
      },
      {
        when: '“What do I have to do beforehand?” —',
        say: 'Your care team sends you a prep plan before the day. It includes your blood work, which is included in the price, and a short liquid-diet period right before the procedure. The dietitian walks you through all of it. It’s simple, and you won’t be doing it alone.',
      },
    ],
  },
  {
    question: 'What can I eat? Do I have to diet?',
    asks: [
      'Will I be able to eat normal food after?',
      'Can I still drink coffee or alcohol?',
      'Am I going to be hungry?',
    ],
    responses: [
      {
        when: 'Standard —',
        say: 'After the procedure your stomach is about 60 to 75% smaller, so you get full on much less and stay full longer. That’s the mechanism, not willpower. You start on a liquid plan with protein shakes, move to soft foods, and then back to regular eating over a few weeks. Your own registered dietitian guides every step and is with you for the full twelve months. Real food, smaller portions, and you’re not hungry all the time.',
      },
      {
        when: '“Can I still have coffee or a glass of wine?” —',
        say: 'Yes, in time. The first few weeks are structured while everything heals, and then it’s about portions rather than a list of banned foods. Your dietitian will tell you exactly when things come back in. The people who do best are the ones who work with the dietitian rather than guessing.',
      },
      {
        when: '“Do I have to diet before?” —',
        say: 'There’s a short liquid-diet period right before the procedure, not weeks of dieting. Your care team gives you the exact plan and the dietitian walks you through it, and it’s all included in the program.',
      },
    ],
  },
  {
    question: 'How much weight will I lose, and does it last?',
    asks: [
      'Does it actually work?',
      'Does it only last five years?',
      'Can I gain it back?',
      'I was on Ozempic and gained it all back — how is this different?',
    ],
    responses: [
      {
        when: 'Standard —',
        say: 'On average, patients lose about 20% of their body weight in the first year. At your weight, that’s around [lb]. Five years out, the average is still 16%, and it’s proven to last for at least ten years and likely longer. What’s your goal?',
      },
      {
        when: '“Does it stop working after ten years?” —',
        say: 'Ten years is how far back the data goes, not an expiry date. The studies have followed patients for a decade and the results have held, so we can say “at least ten years” honestly. The stomach stays smaller, and the twelve months of care is there so the habits stick.',
      },
      {
        when: 'They’ve been on a GLP-1 —',
        say: 'A lot of people come to us from exactly that. GLP-1s are chemical: they change how you feel while you’re on them, and when you stop, most people regain most of the weight because nothing about the stomach changed. ESG is mechanical: it changes your stomach, so it doesn’t wear off. Some patients also combine the two, and the average with medication is 24%. What happened when you came off it?',
      },
    ],
  },
  {
    question: 'Who does the procedure? Are you legitimate?',
    asks: ['Who would be doing this?', 'How many of these have they done?', 'Is it in a hospital or an office?'],
    responses: [
      {
        when: 'Standard —',
        say: 'Your procedure would be at [facility] in [town], which is [an accredited outpatient surgery center / a hospital], with Dr. [name]. Our physicians have performed more than 10,000 of these procedures, and there have been more than 20,000 worldwide. The procedure was developed by Dr. Christopher Thompson, our co-founder, who performed the first ESG in 2012. You’ll meet your doctor before your procedure day.',
      },
      {
        when: '“How many has this doctor done?” —',
        say: 'I’ll give you Dr. [name]’s background right now, and you’ll speak with them before the day. What I can tell you across the team is that our physicians have performed over 10,000 of these, half of all the ESGs done worldwide.',
      },
      {
        when: '“My doctor told me not to” —',
        say: 'That’s worth taking seriously, and our clinical team will want to hear exactly what they said. Bring it to the consult and they’ll go through it with you. Can I get you a time?',
      },
    ],
  },
  {
    question: 'Tell me about the doctor at my center',
    asks: ['Who is the doctor there?', 'What’s their background?', 'Are they any good?'],
    responses: [
      {
        when: 'Find their centre above, then —',
        say: 'You’d be with Dr. [name] at our [city] center. Let me tell you a bit about them.',
      },
    ],
    facts: DOCTOR_LINES,
  },
];

/* -------------------------------------------------------------------------
   Beats
   ------------------------------------------------------------------------- */

/** Three lines, no controls. Who they are and what they rang about. */
const OPEN: PacBeat = {
  id: 'open',
  title: 'Open & build rapport',
  items: [
    { kind: 'action', label: 'Introduce yourself', say: 'Thanks for calling Everself, this is [rep].' },
    { kind: 'action', label: 'Ask their name', say: 'Who do I have the pleasure of speaking with?' },
    { kind: 'action', label: 'Ask for the purpose of the call', say: 'What can I help you with today?' },
    {
      kind: 'group',
      items: [
        { kind: 'script', text: '[name], if you don’t mind me asking, what got you looking into this now?' },
        { kind: 'note', text: 'Use their name once you have it. It is the whole of the rapport on a call this short.' },
      ],
    },
  ],
};

const FAQS: PacBeat = {
  id: 'faqs',
  title: 'Answer FAQs',
  items: [
    {
      kind: 'note',
      text: 'Answer the question they actually asked, well enough that they trust us — then bridge to the consultation. You are not here to cover everything; the consultation is.',
    },
    ...PAC_INTRO_CALL_FAQS.map<PacWidget>((faq) => ({ kind: 'faq', ...faq })),
    { kind: 'action', label: 'Bridge to the consultation', say: BRIDGE },
  ],
};

/**
 * Offering and booking are one section, not two.
 *
 * They were separate on the old card because the offer was the end of a long
 * pitch and the booking was a separate decision. Here the offer *is* the point
 * of the call, and a rep who has just said "would you like me to get you in?"
 * needs the grid under their thumb, not one section further down.
 */
const BOOK: PacBeat = {
  id: 'book',
  title: 'Book the consultation',
  items: [
    { kind: 'action', label: 'Offer the consultation', say: CONSULT },
    // Ask before the grid, not after: a time offered in the wrong zone is worse
    // than a pause to check. The ZIP already sets the grid's zone, but a chart
    // ZIP goes stale and a caller who has moved is not rare.
    { kind: 'action', label: 'Confirm where they are', say: WHERE },
    {
      kind: 'group',
      items: [
        {
          kind: 'note',
          text: 'Skip if it has already come up — it usually has, on the cost or the location question. Then just read the zone back: “so that’s [time] your time.”',
        },
      ],
    },
    { kind: 'slotGrid', id: 'slot' },
    { kind: 'action', label: 'Share what to expect', say: EXPECT },
    {
      kind: 'group',
      items: [
        {
          kind: 'conditionalScript',
          condition: 'If they will not commit to a time —',
          text: 'No problem at all. I’ll email you the booking link so you can pick a time that suits you, and I’ll follow up in a couple of days to see if you have any other questions.',
        },
      ],
    },
  ],
};

/** Internal — nothing here is said on the call. The title says so; no sub-caption. */
const INTERNAL_NOTES: PacBeat = {
  id: 'notes',
  title: 'Internal notes',
  items: [
    { kind: 'select', id: 'close', label: 'Outcome', options: PAC_INTRO_CALL_OUTCOME_OPTIONS },
    // Shared with the PN consultation on purpose: the same caller is read twice,
    // once here and once on the consult, and two vocabularies would make the
    // pair uncomparable.
    { kind: 'multiChips', id: 'motivation', label: 'Motivation', options: PN_MOTIVATION_OPTIONS },
    { kind: 'multiChips', id: 'barrier', label: 'Potential barrier', options: PN_BARRIER_OPTIONS },
    { kind: 'text', id: 'fun_facts', label: 'Fun facts to remember them by' },
  ],
};

/**
 * The intro-call script.
 *
 * Four sections, in the order the call runs:
 *   open → answer FAQs → book the consultation → internal notes
 *
 * A constant rather than a builder: unlike the PN consultation, nothing on this
 * card varies by patient.
 */
export const PAC_INTRO_CALL_SCRIPT: readonly PacBeat[] = [OPEN, FAQS, BOOK, INTERNAL_NOTES];

/** Beat ids, in order — what the collapsed-summary switch is keyed against. */
export const PAC_INTRO_CALL_SECTION_IDS = PAC_INTRO_CALL_SCRIPT.map((beat) => beat.id);
